import {Controller} from "@hotwired/stimulus"
import {disabling} from "./mixins/disabling";
import dispatchSelectLoaded from "../utils/dispatch_select_loaded.js";

/**
 * Alternatives controller allows to display different content based on
 * a <select>'s current value. Not all values must have a content, more pieces
 * of content can depend on the same option. A piece of content can depend
 * on multiple options (any of them needs to be selected).
 *
 * All alternative targets listed in the active item are shown, all others are
 * hidden.
 *
 * %fieldset{ data: { controller: 'alternatives', alternatives_hidden_class: 'hidden' } }
 *
 *  %select{ data: { action: 'change->alternatives#select' } }
 *    %option{value: 'foo'} Foo
 *    %option{value: 'bar zoo'} Bar & Zoo
 *    %option{value: 'zoo'} Zoo
 *
 *  %div{ data: { alternatives_target: 'alternative', alternatives_alternative: 'foo bar' } }
 *    Only displayed when Foo or Bar is selected
 *  %div{ data: { alternatives_target: 'alternative', alternatives_alternative: 'zoo' } }
 *    Only displayed when Zoo is selected
 *
 * Copied from Energo controller of the same name.
 */
export default class extends Controller {
  static targets = ['alternative']
  static classes = ['hidden']

  connect() {
    disabling(this);
    this.element.querySelectorAll('[data-action]').forEach(el => {
      if (!this.scope.containsElement(el)) {
        return
      }

      if (el.dataset['action'].includes(`->alternatives#select`)) {
        this._storePreviousValue(el)
        this.select({target: el, skipConfirmation: true})
      }
    })
  }

  select(event) {
    if (this._changeRequiresConfirmation(event)) {
      if (!window.confirm(event.target.dataset.confirmOnChange)) {
        event.target.value = this._previousValue(event.target)
        dispatchSelectLoaded(event.target)

        return
      }
    }

    this._storePreviousValue(event.target)

    const alternatives = []
    if (event.target.type === 'select-one') {
      Array(...event.target.options).forEach(option => {
        if (option.selected) {
          alternatives.push(option.value.split(' '));
          if ('alternatives' in option.dataset) {
            alternatives.push(option.dataset.alternatives.split(' '));
          }
          this._showAlternatives(alternatives.flat())
        }
      })
    } else {
      console.error(`unsupported toggler for ${this.context.module.identifier}`)
    }

    // create a new event to notify other controllers
    const alternativesEvent = new CustomEvent('alternatives:select', {})
    window.dispatchEvent(alternativesEvent)
  }

  _changeRequiresConfirmation(event) {
    if (event.skipConfirmation || event.target.type !== 'select-one') {
      return false
    }

    return !!event.target.dataset.confirmOnChange && this._previousValue(event.target) !== event.target.value
  }

  _previousValue(select) {
    return select.dataset.previousValue || select.value
  }

  _storePreviousValue(select) {
    if (select?.type === 'select-one') {
      select.dataset.previousValue = select.value
    }
  }

  _showAlternatives(alternativesToShow) {
    this.alternativeTargets.forEach(el => {
      const registeredAlternatives = el.dataset['alternativesAlternative'].split(' ');

      if (registeredAlternatives.filter(v => alternativesToShow.includes(v)).length > 0) {
        this.enable(el);

        /** If we use SelectSearchController,
         * we must send it select:loaded event to create an instance.
         */
        const selectElement = el.querySelector('select');
        if (selectElement) {
            dispatchSelectLoaded(selectElement);
        }
      } else {
        this.disable(el);
      }
    })
  }
}